import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight, Receipt } from "lucide-react";
import { api } from "@/lib/api";
import { toApiError } from "@/lib/api-error";
import { useAuthStore } from "@/lib/auth";
import { formatMoney } from "@/lib/currency";
import { useTenantMe } from "@/lib/tenant-me";
import { PageHeader } from "@/components/v2/PageHeader";
import { StatCard } from "@/components/v2/StatCard";
import {
  DateRangePicker,
  defaultRange,
  toIsoDate,
  type DateRange,
} from "@/components/v2/DateRangePicker";
import { DataTable, type Column } from "@/components/v2/DataTable";
import { EmptyState } from "@/components/v2/EmptyState";
import { BookingSidePanel } from "@/components/v2/BookingSidePanel";
import { EditBookingPanel } from "@/components/v2/EditBookingPanel";

/**
 * Transactions — Pencil frame `waU7J`. Money in (verified payments) + inventory
 * ledger (stock-outs) over a windowed range.
 *
 * Row click opens the related booking's side panel inline (same
 * `?focus=<bookingId>` pattern as `/bookings` and `/clients` so URLs are
 * deep-linkable). `referenceId` on the row is the booking id; rows without
 * one (manual stock adjustments not tied to a booking) are silently inert.
 */

type TxRow = {
  id?: string;
  kind?: string;
  amount?: number | string;
  currency?: string;
  direction?: string;
  note?: string;
  occurredAt?: string;
  referenceId?: string;
};

export function Transactions() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const tenant = useTenantMe();
  const tenantCurrency = tenant.data?.tenant.currency ?? "PHP";
  const [range, setRange] = useState<DateRange>(() => defaultRange());

  // Side-panel deep-link via `?focus=<bookingId>` — same contract as
  // /bookings and /clients. `replace: true` keeps the back button useful.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get("focus");
  const openPanel = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("focus", id);
    else next.delete("focus");
    setSearchParams(next, { replace: true });
  };
  const closePanel = () => openPanel(null);

  // Edit/reschedule overlay — close-then-reopen handoff because two
  // stacked Radix Dialogs deadlock pointer events. Mirrors the pattern
  // in pages/Bookings.tsx.
  const [editId, setEditId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<"edit" | "reschedule">("edit");

  const fromIso = toIsoDate(range.from);
  const toIso = toIsoDate(range.to);
  const feed = useQuery({
    queryKey: ["transactions", fromIso, toIso],
    enabled: !!accessToken,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/transactions", {
        params: { query: { from: fromIso, to: toIso } },
      });
      if (error) throw toApiError(error);
      return data;
    },
  });

  const columns: Column<TxRow>[] = [
    {
      key: "when",
      header: "When",
      sortAccessor: (r) => (r.occurredAt ? new Date(r.occurredAt) : null),
      cell: (r) => (
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {r.occurredAt ? new Date(r.occurredAt).toLocaleString() : "—"}
        </span>
      ),
    },
    {
      key: "kind",
      header: "Kind",
      cell: (r) => (
        <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-mono">
          {r.kind}
        </span>
      ),
    },
    {
      key: "note",
      header: "Note",
      cell: (r) => <span className="text-sm">{r.note}</span>,
    },
    {
      key: "direction",
      header: "Direction",
      align: "right",
      cell: (r) => (
        <span
          className={`inline-flex items-center gap-1 text-xs font-medium ${
            r.direction === "in" ? "text-trend-positive" : "text-trend-negative"
          }`}
        >
          {r.direction === "in" ? (
            <ArrowDownRight className="h-3 w-3" />
          ) : (
            <ArrowUpRight className="h-3 w-3" />
          )}
          {r.direction === "in" ? "Money in" : "Money out"}
        </span>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      sortAccessor: (r) =>
        typeof r.amount === "string" ? Number(r.amount) : (r.amount ?? 0),
      cell: (r) => (
        <span className="font-semibold text-sm">
          {formatMoney(r.currency, r.amount)}
        </span>
      ),
    },
  ];

  return (
    <div className="px-6 py-6 space-y-6">
      <PageHeader
        title="Transactions"
        subtitle="Money in (verified payments) + inventory ledger."
        actions={
          <DateRangePicker value={range} onChange={setRange} align="end" />
        }
      />

      {feed.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {feed.isError && <p className="text-sm text-destructive">{(feed.error as Error).message}</p>}

      {feed.data && (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <StatCard
              label={`Money in · last ${feed.data.windowDays}d`}
              value={formatMoney(tenantCurrency, feed.data.totalMoneyIn, {
                fractionDigits: 0,
              })}
              hint="Verified payments only"
            />
            <StatCard
              label="Entries"
              value={feed.data.rows?.length ?? 0}
              hint="Across payments + inventory"
            />
            <StatCard
              label="Window"
              value={`${feed.data.windowDays}d`}
              hint="Toggle above to change"
            />
          </div>

          <DataTable
            columns={columns}
            rows={(feed.data.rows ?? []) as TxRow[]}
            rowKey={(r) => r.id ?? Math.random().toString()}
            onRowClick={(r) => {
              if (r.referenceId) openPanel(r.referenceId);
            }}
            sortable
            resizable
            hideable
            searchable
            searchPlaceholder="Search transactions…"
            pagination="client"
            pageSize={50}
            exportCsv={{
              filename: `transactions-${new Date().toISOString().slice(0, 10)}.csv`,
            }}
            emptyState={
              <div className="p-8">
                <EmptyState
                  icon={Receipt}
                  title="Nothing yet"
                  description="Verified payments and inventory consumption show up here."
                />
              </div>
            }
          />
        </>
      )}

      <BookingSidePanel
        bookingId={focusId}
        onClose={closePanel}
        onEdit={(id) => {
          // Hand off from view → edit. Close the side panel first so we
          // don't stack two Radix Dialogs (which deadlocks pointer events
          // on the second one). The edit panel's onClose reopens the side
          // panel on the same booking.
          setEditMode("edit");
          setEditId(id);
          openPanel(null);
        }}
        onReschedule={(id) => {
          setEditMode("reschedule");
          setEditId(id);
          openPanel(null);
        }}
      />
      <EditBookingPanel
        bookingId={editId}
        mode={editMode}
        onClose={() => {
          const id = editId;
          setEditId(null);
          // Reopen the side panel on the same booking so the user lands
          // back where they came from with refreshed data.
          if (id) openPanel(id);
        }}
      />
    </div>
  );
}
